import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sendEmailVerification, reload } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getPostLoginRoute } from '../utils/routing';
import { Mail, CheckCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import paymentService from '../services/payment.service';

export default function EmailVerification() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSending, setIsSending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  // REMOVED: Automatic redirect on email verification
  // This was causing redirect loops. Only redirect when user manually clicks "I've verified my email"
  // The App.js routing will handle automatic redirects after verification

  const handleResendVerification = async () => {
    if (!user) return;
    
    try {
      setIsSending(true);
      await sendEmailVerification(user, {
        url: 'https://studystay.be/auth/action',
        handleCodeInApp: false
      });
      toast.success('Verification email sent! Please check your inbox.');
      console.log('✅ Verification email resent to:', user.email);
    } catch (error) {
      console.error('❌ Error sending verification email:', error);
      let errorMessage = 'Failed to send verification email. ';
      if (error.code === 'auth/too-many-requests') {
        errorMessage += 'Too many requests. Please wait a few minutes before trying again.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage += 'User not found. Please log out and register again.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!user) return;
    
    try {
      setIsChecking(true);
      await reload(user);
      
      if (user.emailVerified) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};
        const selectedPlan = userData?.earlyAccessPlan || userData?.selectedPlan || 'starter';
        const role = userData?.role || userData?.userType || userData?.accountType || userData?.type;
        const landlordType = userData?.landlordType || null;

        toast.success('Email verified! Redirecting...');

        if (role === 'landlord' && landlordType === 'private') {
          if (selectedPlan !== 'starter') {
            try {
              const launch = userData?.allowLaunch !== false && !userData?.isLaunchDiscount;
              const checkoutUrl = await paymentService.createSubscriptionCheckout(selectedPlan, launch);
              if (checkoutUrl) {
                window.location.href = checkoutUrl;
                return;
              }
            } catch (checkoutError) {
              console.error('Could not auto-start checkout after verification:', checkoutError);
              toast.error('Kon betaling niet automatisch starten. Kies je plan op de abonnementenpagina.');
            }
          }

          navigate(`/landlord/billing?required=1&plan=${selectedPlan}&source=post-verification`, { replace: true });
          return;
        }

        if (role === 'landlord' && landlordType === 'agency' && selectedPlan !== 'starter') {
          navigate(`/landlord/agency/billing?required=1&plan=${selectedPlan}&source=early-access`, { replace: true });
          return;
        }

        if (role === 'landlord' && selectedPlan !== 'starter') {
          navigate(`/landlord/billing?required=1&plan=${selectedPlan}&source=early-access`, { replace: true });
          return;
        }

        const route = getPostLoginRoute(userData, user.email);
        navigate(route || '/', { replace: true });
      } else {
        toast.error('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Error checking verification:', error);
      toast.error('Failed to check verification status. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Not logged in</h2>
          <p className="text-gray-600 mb-4">Please log in first</p>
          <Link to="/login" className="text-blue-600 hover:underline">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
            <Mail className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Verify Your Email
          </h1>
          <p className="text-gray-600">
            We've sent a verification email to
          </p>
          <p className="text-gray-900 font-semibold mt-1">
            {user.email}
          </p>
        </div>

        <div className="space-y-4 mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Steps to verify:</strong>
            </p>
            <ol className="list-decimal list-inside text-sm text-blue-700 mt-2 space-y-1">
              <li>Check your email inbox (and spam folder)</li>
              <li>Click the verification link in the email</li>
              <li>Return here and click "I've verified my email"</li>
            </ol>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleCheckVerification}
            disabled={isChecking}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isChecking ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Checking...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>I've verified my email</span>
              </>
            )}
          </button>

          <button
            onClick={handleResendVerification}
            disabled={isSending}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Mail className="w-5 h-5" />
                <span>Resend verification email</span>
              </>
            )}
          </button>

          <Link
            to="/login"
            className="block text-center text-gray-600 hover:text-gray-900 transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to login</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

